import { apiClient, type Paginated } from '@/core/api';

import type {
  AgentInstall,
  AgentInstallInput,
  AgentResult,
  AgentResultInput,
  AgentResultParams,
  AgentTemplate,
  AgentTemplateDetail,
} from './types';

// The agents app is mounted at /api/agents/; the routers add the resource
// segment. Install/result detail routes accept public_id OR int id.
const TEMPLATES = '/agents/templates/';
const INSTALLS = '/agents/installs/';
const RESULTS = '/agents/results/';

// The catalog + a tenant's installs are small, bounded sets (one row per agent,
// ~25 today). The API paginates at PAGE_SIZE=20, so request the whole set in one
// page — otherwise the roster silently shows only the first 20 (the last
// direction groups drop off). Well above any realistic catalog size.
const CATALOG_PAGE_SIZE = 500;

// ---- Templates (global catalog; icon upload = Founder/Super-Admin) ----
export const templatesApi = {
  list: async (): Promise<AgentTemplate[]> =>
    (
      await apiClient.get<Paginated<AgentTemplate>>(TEMPLATES, {
        params: { page_size: CATALOG_PAGE_SIZE },
      })
    ).data.results,
  get: async (id: string | number): Promise<AgentTemplateDetail> =>
    (await apiClient.get<AgentTemplateDetail>(`${TEMPLATES}${id}/`)).data,
  uploadImage: async (id: string | number, file: File): Promise<AgentTemplate> => {
    const form = new FormData();
    form.append('image', file);
    return (await apiClient.post<AgentTemplate>(`${TEMPLATES}${id}/image/`, form)).data;
  },
  clearImage: async (id: string | number): Promise<AgentTemplate> =>
    (await apiClient.delete<AgentTemplate>(`${TEMPLATES}${id}/image/`)).data,
};

// ---- Installs (per-tenant; reads = any staff, writes = Founder/Super-Admin) ----
export const installsApi = {
  list: async (params: Record<string, unknown> = {}): Promise<AgentInstall[]> =>
    (
      await apiClient.get<Paginated<AgentInstall>>(INSTALLS, {
        params: { page_size: CATALOG_PAGE_SIZE, ...params },
      })
    ).data.results,
  get: async (id: string | number): Promise<AgentInstall> =>
    (await apiClient.get<AgentInstall>(`${INSTALLS}${id}/`)).data,
  create: async (input: AgentInstallInput): Promise<AgentInstall> =>
    (await apiClient.post<AgentInstall>(INSTALLS, input)).data,
  update: async (id: string | number, input: AgentInstallInput): Promise<AgentInstall> =>
    (await apiClient.patch<AgentInstall>(`${INSTALLS}${id}/`, input)).data,
  remove: async (id: string | number): Promise<void> => {
    await apiClient.delete(`${INSTALLS}${id}/`);
  },
};

// ---- Results (in-product history; author-private until shared) ----
export const resultsApi = {
  list: async (params: AgentResultParams = {}): Promise<AgentResult[]> =>
    (await apiClient.get<Paginated<AgentResult>>(RESULTS, { params })).data.results,
  get: async (id: string | number): Promise<AgentResult> =>
    (await apiClient.get<AgentResult>(`${RESULTS}${id}/`)).data,
  update: async (id: string | number, input: AgentResultInput): Promise<AgentResult> =>
    (await apiClient.patch<AgentResult>(`${RESULTS}${id}/`, input)).data,
};
